import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { Child, ChildDaycare, ChildSection } from '@/types/Child'
import { Parent } from '@/types/Parent'
import { Section } from '@/types/Section'
import { Daycare } from '@/types/Daycare'
import { ParentChecked, SectionAndParent } from './types'

interface UseParentSelectionProps {
    daycare?: Daycare
    sections?: Section[]
    parents?: Parent[]
    postChilds: number[]
    setPostChilds: Dispatch<SetStateAction<number[]>>
}

export interface UseParentSelectionReturn {
    sectionAndParents: SectionAndParent[]
    parentsWithoutSection: ParentChecked[]
    handleSectionChange: (sectionIndex: number) => void
    handleParentChange: (sectionId: number, parentId: number) => void
    handleWithoutSectionChange: () => void
    handleParentWithoutSectionChange: (parentId: number) => void
}

export function useParentSelection({
    daycare,
    sections,
    parents,
    postChilds,
    setPostChilds
}: UseParentSelectionProps): UseParentSelectionReturn {
    const [sectionAndParents, setSectionAndParents] = useState<SectionAndParent[]>([])
    const [parentsWithoutSection, setParentsWithoutSection] = useState<ParentChecked[]>([])

    const createParentChecked: (parent: Parent, childs: Child[], defaultCheck?: boolean) => ParentChecked = useCallback(
        (parent: Parent, childs: Child[], defaultCheck: boolean = true): ParentChecked => ({
            parent,
            childs,
            isCheck:
                postChilds.length > 0 ? childs.every((c: Child): boolean => postChilds.includes(c.id)) : defaultCheck
        }),
        [postChilds]
    )

    const getChildIdsForParentInSection: (parent: Parent, sectionId?: number) => number[] = useCallback(
        (parent: Parent, sectionId?: number): number[] => {
            return parent.childs
                .filter((child: Child): boolean => {
                    const childDaycare: ChildDaycare | undefined = child.daycares.find(
                        (cd: ChildDaycare): boolean =>
                            cd.daycareId === daycare?.id &&
                            cd.visible &&
                            (sectionId
                                ? cd.sections.length > 0 &&
                                  cd.sections.some(
                                      (s: ChildSection): boolean => s.sectionId === sectionId && s.isPrimary
                                  )
                                : cd.sections.length === 0)
                    )
                    return childDaycare !== undefined
                })
                .map((child: Child): number => child.id)
        },
        [daycare?.id]
    )

    useEffect(() => {
        if (!daycare || !parents || !sections) return

        const newParentsWithoutSection: ParentChecked[] = parents.flatMap((parent: Parent): ParentChecked[] => {
            const childWithoutSection: Child[] = parent.childs.filter((child: Child): boolean => {
                const childDaycare: ChildDaycare | undefined = child.daycares.find(
                    (cd: ChildDaycare): boolean => cd.daycareId === daycare.id && cd.visible && cd.sections.length === 0
                )
                return childDaycare !== undefined
            })

            if (childWithoutSection.length === 0) return []
            return [createParentChecked(parent, childWithoutSection, false)]
        })

        setParentsWithoutSection(newParentsWithoutSection)

        const newSectionAndParents: SectionAndParent[] = sections
            .map((section: Section): SectionAndParent => {
                const matchedParents: ParentChecked[] = parents
                    .map((parent: Parent): ParentChecked | null => {
                        const childMatches: Child[] = parent.childs.filter((child: Child): boolean => {
                            const childDaycare: ChildDaycare | undefined = child.daycares.find(
                                (cd: ChildDaycare): boolean =>
                                    cd.daycareId === daycare.id &&
                                    cd.visible &&
                                    cd.sections.length > 0 &&
                                    cd.sections.some(
                                        (s: ChildSection): boolean => s.sectionId === section.id && s.isPrimary
                                    )
                            )
                            return childDaycare !== undefined
                        })
                        return childMatches.length > 0 ? createParentChecked(parent, childMatches, true) : null
                    })
                    .filter((p: ParentChecked | null): p is ParentChecked => p !== null)

                return { section, parents: matchedParents }
            })
            .filter((s: SectionAndParent): boolean => s.parents.length > 0)

        setSectionAndParents(newSectionAndParents)

        const allSelectedChildIds: number[] = newSectionAndParents.flatMap((sap: SectionAndParent): number[] =>
            sap.parents
                .filter((p: ParentChecked): boolean => p.isCheck)
                .flatMap((p: ParentChecked): number[] => getChildIdsForParentInSection(p.parent, sap.section.id))
        )
        setPostChilds(allSelectedChildIds)
    }, [daycare, parents, sections])

    const handleSectionChange: (sectionIndex: number) => void = useCallback(
        (sectionIndex: number): void => {
            const section: SectionAndParent = sectionAndParents[sectionIndex]
            const allChecked: boolean = section.parents.every((p: ParentChecked): boolean => p.isCheck)
            const childIds: number[] = section.parents.flatMap((p: ParentChecked): number[] =>
                getChildIdsForParentInSection(p.parent, section.section.id)
            )

            setSectionAndParents((prev: SectionAndParent[]): SectionAndParent[] => {
                const newSections: SectionAndParent[] = [...prev]
                newSections[sectionIndex] = {
                    ...newSections[sectionIndex],
                    parents: newSections[sectionIndex].parents.map(
                        (p: ParentChecked): ParentChecked => ({
                            ...p,
                            isCheck: !allChecked
                        })
                    )
                }
                return newSections
            })

            setPostChilds((prev: number[]): number[] => {
                if (!allChecked) {
                    return [...new Set([...prev, ...childIds])]
                } else {
                    return prev.filter((id: number): boolean => !childIds.includes(id))
                }
            })
        },
        [sectionAndParents, getChildIdsForParentInSection, setPostChilds]
    )

    const handleParentChange: (sectionId: number, parentId: number) => void = useCallback(
        (sectionId: number, parentId: number): void => {
            const sectionIndex: number = sectionAndParents.findIndex(
                (sap: SectionAndParent): boolean => sap.section.id === sectionId
            )
            if (sectionIndex === -1) return

            const parentIndex: number = sectionAndParents[sectionIndex].parents.findIndex(
                (p: ParentChecked): boolean => p.parent.id === parentId
            )
            if (parentIndex === -1) return

            const parent: ParentChecked = sectionAndParents[sectionIndex].parents[parentIndex]
            const childIds: number[] = getChildIdsForParentInSection(parent.parent, sectionId)
            const isCurrentlyChecked: boolean = parent.isCheck

            setSectionAndParents((prev: SectionAndParent[]): SectionAndParent[] =>
                prev.map(
                    (sap: SectionAndParent, sIdx: number): SectionAndParent =>
                        sIdx !== sectionIndex
                            ? sap
                            : {
                                  ...sap,
                                  parents: sap.parents.map(
                                      (p: ParentChecked, pIdx: number): ParentChecked =>
                                          pIdx === parentIndex ? { ...p, isCheck: !p.isCheck } : p
                                  )
                              }
                )
            )

            setPostChilds((prev: number[]): number[] => {
                if (isCurrentlyChecked) {
                    return prev.filter((id: number): boolean => !childIds.includes(id))
                } else {
                    return [...prev, ...childIds]
                }
            })
        },
        [sectionAndParents, getChildIdsForParentInSection, setPostChilds]
    )

    const handleWithoutSectionChange: () => void = useCallback((): void => {
        const allChecked: boolean = parentsWithoutSection.every((p: ParentChecked): boolean => p.isCheck)
        const childIds: number[] = parentsWithoutSection.flatMap((p: ParentChecked): number[] =>
            getChildIdsForParentInSection(p.parent)
        )

        setParentsWithoutSection((prev: ParentChecked[]): ParentChecked[] =>
            prev.map((p: ParentChecked): ParentChecked => ({ ...p, isCheck: !allChecked }))
        )

        setPostChilds((prev: number[]): number[] => {
            if (!allChecked) {
                return [...new Set([...prev, ...childIds])]
            } else {
                return prev.filter((id: number): boolean => !childIds.includes(id))
            }
        })
    }, [parentsWithoutSection, getChildIdsForParentInSection, setPostChilds])

    const handleParentWithoutSectionChange: (parentId: number) => void = useCallback(
        (parentId: number): void => {
            const parentIndex: number = parentsWithoutSection.findIndex(
                (p: ParentChecked): boolean => p.parent.id === parentId
            )
            if (parentIndex === -1) return

            const parent: ParentChecked = parentsWithoutSection[parentIndex]
            const childIds: number[] = getChildIdsForParentInSection(parent.parent)
            const isCurrentlyChecked: boolean = parent.isCheck

            setParentsWithoutSection((prev: ParentChecked[]): ParentChecked[] =>
                prev.map(
                    (p: ParentChecked, idx: number): ParentChecked =>
                        idx === parentIndex ? { ...p, isCheck: !p.isCheck } : p
                )
            )

            setPostChilds((prev: number[]): number[] => {
                if (isCurrentlyChecked) {
                    return prev.filter((id: number): boolean => !childIds.includes(id))
                } else {
                    return [...prev, ...childIds]
                }
            })
        },
        [parentsWithoutSection, getChildIdsForParentInSection, setPostChilds]
    )

    return {
        sectionAndParents,
        parentsWithoutSection,
        handleSectionChange,
        handleParentChange,
        handleWithoutSectionChange,
        handleParentWithoutSectionChange
    }
}
